import { Vector3 } from 'three/webgpu';
import type { Motion } from './Attack';
import { strikeSwing, type Weapon } from './weapons';

/**
 * What a hit needs to know about the enemy. Which bonus pays off depends on these more than
 * on the weapon:
 * - armor and a shield: finesse (armor is pierced by it; a shield must be got round);
 * - lots of hp: rotation (the most damage per second);
 * - medium hp, low poise and mass: movement (one big blow that stuns and knocks flying);
 * - tiny hp: nothing (any hit will do).
 */
export interface Target {
  /** Its centre (universe), how far it moved during the strike (m), and its body's radius (m). */
  center: Vector3;
  travel: Vector3;
  radius: number;
  /** Share of damage its armor stops (0–1), unless finesse finds the gaps. */
  armor: number;
  /** The side its shield covers (unit, across its up), how wide (half-angle, radians), and how much it stops. */
  shieldDir: Vector3 | null;
  shieldHalf: number;
  shieldBlock: number;
  /** kg, and how much of a knock it shrugs off (0–1). */
  mass: number;
  knockResist: number;
  /** Share of the movement bonus it soaks up (soft or huge bodies). */
  cushion: number;
  /** A hit's stun power must beat this to stun it. */
  poise: number;
}

export interface HitResult {
  damage: number;
  /** It hit the shield. */
  blocked: boolean;
  /** A critical hit's bonus (0.4 is ×1.4), or 0; and the share of armor it got through. */
  crit: number;
  pierce: number;
  /** Speed to add to the enemy (m/s, universe), and seconds it's stunned. */
  push: Vector3;
  stun: number;
  /** The bonuses as this weapon scored them, and the metres closed on the target during the strike. */
  finesse: number;
  rotation: number;
  movement: number;
  closed: number;
  /** What made this hit what it was, for the HUD and tuning. */
  notes: string[];
}

/**
 * A feint (Motion.feint) of this share of the weapon's strike swing scores 1 for finesse: going
 * out and back over the whole strike (half the budget each way), turning away at the release
 * scores more.
 */
const FINESSE_FULL = 0.5;
/**
 * Rotation: each degree the swing turns from the release to the hit adds 2% damage before the
 * weapon's affinity, however long the weapon's strike: a heavy weapon's longer strike has time
 * to build more swing, as it has to build more momentum.
 */
const ROTATION_PER_DEGREE = 0.02;
/**
 * Movement: metres closed on the target during the strike (yours toward it, and its toward
 * you) for a score of 1; each point adds MOVEMENT_GAIN. A longer strike has time to close more.
 * Your travel and the target's each count at most TRAVEL_MAX m, and all of it CLOSED_MAX m, so a
 * teleport can't make a huge hit.
 */
const MOVE_FULL = 2;
const MOVEMENT_GAIN = 0.5;
const TRAVEL_MAX = 5;
const CLOSED_MAX = 10;
/**
 * Coming down counts PLUNGE_GAIN times, onto a target whose centre is no more than
 * PLUNGE_ABOVE (the sine of its elevation from the eye) above you.
 */
const PLUNGE_GAIN = 2;
const PLUNGE_ABOVE = 0.3;
/** A full charge multiplies the whole hit by 1 + CHARGE_GAIN. */
const CHARGE_GAIN = 0.5;
/**
 * The other bonuses add up: × (1 + rotation + movement + precision). Finesse finds the gaps in
 * armor: PRECISION × armor × finesse.
 */
const PRECISION = 0.8;
/**
 * The finesse circle (drawn round the finesse angle after the release). There is none when the
 * aim at the release is already within CIRCLE_CLEAR of its centre (Motion.circle): no crit then,
 * though finesse still pierces armor. Once the aim has been at least CRIT_ARM from it during the
 * strike, ending the strike on it counts:
 * - within CRIT_CLOSE it pierces more and more armor, all of it within CRIT_RADIUS;
 * - within CRIT_RADIUS it's a critical hit: × (1 + (CRIT_PER_DEGREE × the farthest the aim got
 *   from it, in degrees, up to CRIT_MAX_DEGREES, + up to CRIT_CENTER for how close to its
 *   centre) × the weapon's crit affinity). Glancing and blocked hits can't be critical.
 */
export const CRIT_RADIUS = (4 * Math.PI) / 180;
export const CIRCLE_CLEAR = (5 * Math.PI) / 180;
export const CRIT_ARM = (10 * Math.PI) / 180;
const CRIT_CLOSE = (15 * Math.PI) / 180;
const CRIT_PER_DEGREE = 0.01;
const CRIT_MAX_DEGREES = 90;
const CRIT_CENTER = 0.1;
/** A glancing hit's share of damage, knockback and stun. */
const GLANCING = 0.5;
/** Aim this far past a body's radius still lands on its edge (m). */
const EDGE = 0.25;
const REFERENCE_MASS = 80;

/**
 * Knockback: a base from the weapon (charge, movement and rotation scale it), plus SWING_KNOCK ×
 * weapon weight × the tip's speed (counted up to MAX_TIP m/s), all × KNOCK_GAIN, for 80 kg. It
 * never throws anything faster than MAX_KNOCK m/s. Hits lift enemies off the ground a little (LIFT).
 */
const SWING_KNOCK = 0.35;
const MAX_TIP = 20;
const KNOCK_GAIN = 1.6;
const MAX_KNOCK = 30;
const LIFT = 0.3;

const _to = new Vector3();
const _rel = new Vector3();
const _theirs = new Vector3();
const _dir = new Vector3();
const _side = new Vector3();
const _right = new Vector3();
const _at = new Vector3();

/**
 * Damage, knockback, stun, a critical and whether the shield stopped it, from the weapon, how
 * the player moved during the attack, and the enemy. Every weapon earns all three bonuses,
 * scaled by its affinity for each. Pure: no state, so it can be tested and tuned alone.
 */
export function resolveHit(w: Weapon, m: Motion, t: Target, glancing = false): HitResult {
  const notes: string[] = [];
  const aff = w.affinity;
  const degrees = (m.arc * 180) / Math.PI;
  const rotation = aff.rotation * ROTATION_PER_DEGREE * degrees;
  const tip = Math.min(MAX_TIP, m.swing.length());
  const finesse = aff.finesse * Math.min(1.5, m.feint / (FINESSE_FULL * strikeSwing(w)));
  // Metres closed on the target during the strike: yours toward it and its toward you, each
  // capped. Coming down (along gravity, relative to it) counts double, onto anything not above
  // you: a plunging attack carries your weight with it.
  _dir.subVectors(t.center, m.eye).normalize();
  _rel.copy(m.travel).clampLength(0, TRAVEL_MAX);
  _rel.sub(_theirs.copy(t.travel).clampLength(0, TRAVEL_MAX));
  const down = Math.max(0, -_rel.dot(m.up));
  const plunge = _dir.dot(m.up) < PLUNGE_ABOVE ? down : 0;
  const closed = Math.min(
    CLOSED_MAX,
    Math.max(0, _rel.addScaledVector(m.up, down).dot(_dir)) + PLUNGE_GAIN * plunge,
  );
  _rel.addScaledVector(m.up, -down);
  const movement = aff.movement * (closed / MOVE_FULL) * (1 - t.cushion);

  const chargeGain = 1 + CHARGE_GAIN * m.charge;
  if (m.charge > 0.05) notes.push(`charge ${Math.round(m.charge * 100)}% ×${chargeGain.toFixed(2)}`);
  const rotationBonus = rotation;
  const movementBonus = MOVEMENT_GAIN * movement;
  if (rotation > 0.02) notes.push(`swing ${Math.round(degrees)}° +${Math.round(rotationBonus * 100)}%`);
  if (movement > 0.05) notes.push(`closed ${closed.toFixed(1)} m +${Math.round(movementBonus * 100)}%`);
  // Finesse finds the gaps in armor (a bonus that adds).
  const precisionBonus = PRECISION * t.armor * Math.min(1.5, finesse);
  let damage = w.damage * chargeGain * (1 + rotationBonus + movementBonus + precisionBonus);

  // The finesse circle: how far the aim is from it at the hit, and whether it went far enough
  // out from it first to count.
  const offCircle = m.forward.angleTo(m.finesseDir);
  const armed = m.circle && m.feintFar >= CRIT_ARM;
  const onCircle = armed ? 1 - smoothstep(CRIT_RADIUS, CRIT_CLOSE, offCircle) : 0;

  // Armor stops a share, unless finesse, or ending on the finesse circle, pierces it.
  const pierce = Math.max(Math.min(1, finesse), onCircle);
  if (t.armor > 0) {
    damage *= 1 - t.armor * (1 - pierce);
    if (pierce > 0.05) {
      const why = onCircle > Math.min(1, finesse) ? 'on the circle' : `finesse ${finesse.toFixed(1)}`;
      notes.push(`${why}: ${Math.round(pierce * 100)}% through armor +${Math.round(precisionBonus * 100)}%`);
    } else notes.push(`armor −${Math.round(t.armor * 100)}%`);
  } else if (finesse > 0.1) notes.push('finesse (no armor to beat)');

  // The shield stops what lands on it: where the attack meets the body must be off its side.
  let blocked = false;
  if (t.shieldDir) {
    contactDir(m, t, 0, _to);
    blocked = _to.angleTo(t.shieldDir) < t.shieldHalf;
    notes.push(blocked ? 'blocked' : 'round the shield');
  }
  if (blocked) damage *= 1 - t.shieldBlock;
  if (glancing) {
    damage *= GLANCING;
    notes.push(`glancing −${Math.round((1 - GLANCING) * 100)}%`);
  }

  // A critical: ending the strike inside the finesse circle, squarely and off the shield.
  let crit = 0;
  if (armed && offCircle <= CRIT_RADIUS && !blocked && !glancing) {
    const farDegrees = Math.min(CRIT_MAX_DEGREES, (m.feintFar * 180) / Math.PI);
    crit = (CRIT_PER_DEGREE * farDegrees + CRIT_CENTER * (1 - offCircle / CRIT_RADIUS)) * aff.crit;
    damage *= 1 + crit;
    notes.push(`CRITICAL ×${(1 + crit).toFixed(2)} (${Math.round(farDegrees)}° out)`);
  }

  // Knockback and stun come from movement and from how hard the weapon was swung.
  let knock =
    (w.knockback * (0.8 + 0.8 * m.charge + 1.3 * movement + 0.8 * rotation) + SWING_KNOCK * w.weight * tip) *
    KNOCK_GAIN *
    (REFERENCE_MASS / t.mass) *
    (1 - t.knockResist) *
    (blocked ? 0.3 : 1) *
    (glancing ? GLANCING : 1);
  const power =
    w.weight *
    (0.3 * m.charge + movement + 0.6 * rotation + 0.04 * tip) *
    (blocked ? 0.4 : 1) *
    (glancing ? GLANCING : 1);
  const stun = power > t.poise ? Math.min(2.5, 0.5 + (power - t.poise)) : 0;
  if (stun > 0) notes.push(`stunned ${stun.toFixed(1)} s`);
  else if (power > 1) notes.push('shrugs it off');

  // The way the weapon was swung (an uppercut throws up, a cut sideways), else away from you;
  // plus the closing motion, and a little lift.
  const push = new Vector3();
  flatDirection(m.eye, t.center, m.up, m.forward, push);
  if (tip > 0.5) {
    const s = tip / (tip + 3);
    // The blade meets it out in front: a swing curling back over your head (the end of an
    // uppercut) still throws it up and away, not back at you.
    _side.copy(m.swing).normalize();
    const back = _side.dot(push);
    if (back < 0) _side.addScaledVector(push, -back).normalize();
    push.multiplyScalar(1 - s).addScaledVector(_side, 1.5 * s);
  }
  _rel.addScaledVector(m.up, -_rel.dot(m.up));
  if (_rel.lengthSq() > 0.01) push.addScaledVector(_rel.normalize(), 0.5 * movement);
  push.normalize().addScaledVector(m.up, LIFT);
  if (tip > 0.5) notes.push(`swung ${tip.toFixed(0)} m/s`);
  knock = knock < 0.05 ? 0 : Math.min(MAX_KNOCK, knock);
  push.normalize().multiplyScalar(knock);
  return { damage, blocked, crit, pierce, push, stun, finesse, rotation, movement, closed, notes };
}

/**
 * Which side of the target (unit, across up, from its centre) the attack meets: where the aim
 * passes it at the hit (or `shift` metres further right: a shield's guess).
 */
export function contactDir(
  m: Pick<Motion, 'eye' | 'forward' | 'up'>,
  t: Pick<Target, 'center' | 'radius'>,
  shift: number,
  out: Vector3,
): Vector3 {
  const up = m.up;
  // Toward the attacker, and the attacker's right.
  flatDirection(t.center, m.eye, up, m.forward, out);
  flat(m.forward, up, _right);
  _right.cross(up).normalize();
  // How far to the right of the target's centre the aim passes.
  const offset = -_at.subVectors(t.center, m.eye).dot(_right) + shift;
  const phi = Math.max(-1, Math.min(1, offset / (t.radius + EDGE))) * (Math.PI / 2);
  return out.multiplyScalar(Math.cos(phi)).addScaledVector(_right, Math.sin(phi)).normalize();
}

function smoothstep(a: number, b: number, x: number): number {
  const t = Math.max(0, Math.min(1, (x - a) / (b - a)));
  return t * t * (3 - 2 * t);
}

/**
 * Unit direction from `from` to `to` along the ground (across `up`); `fallback` flattened if
 * they coincide.
 */
function flatDirection(from: Vector3, to: Vector3, up: Vector3, fallback: Vector3, out: Vector3): Vector3 {
  out.subVectors(to, from);
  out.addScaledVector(up, -out.dot(up));
  if (out.lengthSq() < 1e-6) out.copy(fallback).addScaledVector(up, -fallback.dot(up));
  return out.normalize();
}

/** `v` flattened onto the ground plane (across `up`), unit. */
function flat(v: Vector3, up: Vector3, out: Vector3): Vector3 {
  return out.copy(v).addScaledVector(up, -v.dot(up)).normalize();
}
